import React from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { getActivities, getHealthRange, getProfile } from '../api/trainer';
import { Activity, Profile } from '../api/types';
import ChartCard from '../components/ChartCard';
import DistanceVolumeChart from '../components/DistanceVolumeChart';
import PageHeader from '../components/PageHeader';
import StatusMessage from '../components/StatusMessage';
import { useAsync } from '../hooks/useAsync';
import { formatDuration, formatNumber, formatPace, isoDaysAgo, isoLocal, shortDate } from '../utils/format';
import { isRun } from '../utils/training';
import { Y_AXIS, yAxisProps } from '../utils/axis';

const AXIS = { stroke: 'var(--text-muted)', fontSize: 12 };
const MARGIN = { top: 8, right: 8, bottom: 0, left: -12 };
const TREND_DAYS = 90;
/** VO2max and running dynamics move slowly, so they are shown over a longer window. */
const LONG_DAYS = 365;

const EMPTY_PROFILE: Profile = { hrZones: [], fitnessAge: [] };

/** The zone rows of a sport's heart rate zones: from each floor up to the next floor, the last up to max. */
function zoneRows(zones: Profile['hrZones'][number] | undefined) {
  if (!zones) return [];
  const floors = [zones.zone1Floor, zones.zone2Floor, zones.zone3Floor, zones.zone4Floor, zones.zone5Floor];
  return floors.flatMap((floor, i) => {
    if (floor == null) return [];
    const next = i < 4 ? floors[i + 1] : zones.maxHr;
    return [{ zone: i + 1, from: floor, to: next ?? null }];
  });
}

const ZONE_NAMES = ['Warm-up', 'Easy', 'Aerobic', 'Threshold', 'Maximum'];

/** Every page of activities since `from` (capped). */
async function activitiesSince(from: string): Promise<Activity[]> {
  const all: Activity[] = [];
  for (let page = 0; page < 5; page++) {
    const result = await getActivities({ from, page, size: 100 });
    all.push(...result.items);
    if (page + 1 >= result.totalPages) break;
  }
  return all;
}

const FitnessPage: React.FC = () => {
  const data = useAsync(async () => {
    const from = isoDaysAgo(TREND_DAYS);
    const to = isoDaysAgo(0);
    const [longActivities, daily, hrv, battery, profile] = await Promise.all([
      activitiesSince(isoDaysAgo(LONG_DAYS)),
      getHealthRange('daily', { from, to }),
      getHealthRange('hrv', { from, to }),
      getHealthRange('body-battery', { from, to }),
      getProfile().catch(() => EMPTY_PROFILE),
    ]);
    const activities = longActivities.filter(a => a.startTime >= `${from}T00:00:00`);
    const allRuns = longActivities.filter(isRun).sort((x, y) => x.startTime.localeCompare(y.startTime));
    const runs = allRuns.filter(r => r.startTime >= `${from}T00:00:00`);
    const dayOf = (r: Activity) => shortDate(isoLocal(new Date(r.startTime)));
    const withValue = (pick: (r: Activity) => number | null) =>
      allRuns.filter(r => pick(r) != null).map(r => ({ date: dayOf(r), value: pick(r) }));
    const vo2 = allRuns.filter(r => r.vo2Max != null);
    return {
      profile,
      vo2Rows: vo2.map(r => ({ date: dayOf(r), vo2: r.vo2Max })),
      latestVo2: vo2.length ? vo2[vo2.length - 1].vo2Max : null,
      firstVo2: vo2.length ? vo2[0].vo2Max : null,
      lactateThreshold: [...allRuns].reverse().find(r => r.lactateThresholdBpm != null)?.lactateThresholdBpm ?? null,
      strideRows: withValue(r => r.avgStrideLengthCm),
      contactRows: withValue(r => r.avgGroundContactMs),
      oscillationRows: withValue(r => r.avgVerticalOscillationCm),
      powerRows: withValue(r => r.avgPowerW),
      effectRows: allRuns
        .filter(r => r.aerobicTrainingEffect != null || r.anaerobicTrainingEffect != null)
        .map(r => ({ date: dayOf(r), aerobic: r.aerobicTrainingEffect, anaerobic: r.anaerobicTrainingEffect })),
      runs,
      totalKm: activities.reduce((sum, a) => sum + (a.distanceM ?? 0), 0) / 1000,
      paceRows: runs
        .filter(r => r.avgPaceSPerKm != null)
        .map(r => ({ date: shortDate(isoLocal(new Date(r.startTime))), pace: r.avgPaceSPerKm })),
      hrRows: runs
        .filter(r => r.avgHr != null)
        .map(r => ({ date: shortDate(isoLocal(new Date(r.startTime))), hr: r.avgHr })),
      restingRows: daily.map(d => ({ date: shortDate(d.date), resting: d.restingHr })),
      hrvRows: hrv.map(d => ({ date: shortDate(d.date), weekly: d.weeklyAvg, lastNight: d.lastNightAvg })),
      batteryRows: battery.map(d => ({ date: shortDate(d.date), high: d.highest, low: d.lowest })),
    };
  }, []);

  const d = data.data;
  const runningZones = d?.profile.hrZones.find(z => z.sport === 'RUNNING') ?? d?.profile.hrZones.find(z => z.sport === 'DEFAULT') ?? d?.profile.hrZones[0];
  const zones = zoneRows(runningZones);
  const fitnessAge = d?.profile.fitnessAge[d.profile.fitnessAge.length - 1];
  const averagePace = d && d.paceRows.length
    ? d.paceRows.reduce((sum, r) => sum + (r.pace ?? 0), 0) / d.paceRows.length
    : null;

  return (
    <>
      <PageHeader title="Fitness">
        <p>
          VO2max, fitness age, heart rate zones, running form, pace and heart rate trends, weekly volume and recovery
          signals.
        </p>
      </PageHeader>


      <StatusMessage loading={data.loading} error={data.error} onRetry={data.reload} />

      {d && (
        <>
          <Row className="g-3 mb-3">
            <Col xs={6} md={4}>
              <section className="surface">
                <div className="stat-label">Runs, last {TREND_DAYS} days</div>
                <div className="stat-value tabular">{d.runs.length}</div>
                <div className="stat-hint">{formatNumber(d.totalKm, 1)} km in all activities</div>
              </section>
            </Col>
            <Col xs={6} md={4}>
              <section className="surface">
                <div className="stat-label">Average run pace</div>
                <div className="stat-value tabular">{averagePace == null ? '—' : formatPace(averagePace)}</div>
                <div className="stat-hint">mean of run averages</div>
              </section>
            </Col>
            <Col xs={12} md={4}>
              <section className="surface">
                <div className="stat-label">Resting HR (latest)</div>
                <div className="stat-value tabular">
                  {formatNumber(d.restingRows[d.restingRows.length - 1]?.resting)} bpm
                </div>
                <div className="stat-hint">HRV 7-day avg {formatNumber(d.hrvRows[d.hrvRows.length - 1]?.weekly)} ms</div>
              </section>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">VO2max (latest run)</div>
                <div className="stat-value tabular">{d.latestVo2 == null ? '—' : formatNumber(d.latestVo2, 1)}</div>
                <div className="stat-hint">
                  {d.latestVo2 != null && d.firstVo2 != null && d.vo2Rows.length > 1
                    ? `${d.latestVo2 - d.firstVo2 >= 0 ? '+' : ''}${formatNumber(d.latestVo2 - d.firstVo2, 1)} over the last year`
                    : 'ml/kg/min'}
                </div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Fitness age</div>
                <div className="stat-value tabular">
                  {fitnessAge?.fitnessAge == null ? '—' : formatNumber(fitnessAge.fitnessAge, 1)}
                </div>
                <div className="stat-hint">
                  {fitnessAge?.chronologicalAge != null ? `age ${fitnessAge.chronologicalAge}` : 'Garmin estimate'}
                  {fitnessAge?.achievableFitnessAge != null && `, achievable ${formatNumber(fitnessAge.achievableFitnessAge, 1)}`}
                </div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Lactate threshold HR</div>
                <div className="stat-value tabular">
                  {(d.lactateThreshold ?? runningZones?.lactateThresholdHr) == null
                    ? '—'
                    : `${d.lactateThreshold ?? runningZones?.lactateThresholdHr} bpm`}
                </div>
                <div className="stat-hint">from your latest runs</div>
              </section>
            </Col>
            <Col xs={6} md={3}>
              <section className="surface">
                <div className="stat-label">Max HR used for zones</div>
                <div className="stat-value tabular">{runningZones?.maxHr == null ? '—' : `${runningZones.maxHr} bpm`}</div>
                <div className="stat-hint">{runningZones?.trainingMethod ?? 'heart rate zones'}</div>
              </section>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={6}>
              <ChartCard title="Run pace (s/km, lower is faster)" empty={d.paceRows.length === 0}>
                <LineChart data={d.paceRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.paceRows, ['pace'], { kind: 'time' })} reversed tickFormatter={v => formatDuration(Math.round(v))} width={52} />
                  <Tooltip />
                  <Line type="monotone" dataKey="pace" name="Avg pace" stroke="var(--chart-2)" strokeWidth={2} dot />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Run heart rate (avg bpm)" empty={d.hrRows.length === 0}>
                <LineChart data={d.hrRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.hrRows, ['hr'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="hr" name="Avg HR" stroke="var(--chart-4)" strokeWidth={2} dot />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="VO2max estimate (last year)" empty={d.vo2Rows.length === 0}>
                <LineChart data={d.vo2Rows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.vo2Rows, ['vo2'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="vo2" name="VO2max" stroke="var(--chart-1)" strokeWidth={2} dot />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Training effect (0 to 5)" empty={d.effectRows.length === 0}>
                <LineChart data={d.effectRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(undefined, [], { fixed: [0, 5] })} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="aerobic" name="Aerobic" stroke="var(--chart-1)" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="anaerobic" name="Anaerobic" stroke="var(--chart-3)" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Stride length (cm)" empty={d.strideRows.length === 0}>
                <LineChart data={d.strideRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.strideRows, ['value'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" name="Stride length" stroke="var(--series-5)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Ground contact time (ms)" empty={d.contactRows.length === 0}>
                <LineChart data={d.contactRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.contactRows, ['value'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" name="Ground contact" stroke="var(--series-7)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Vertical oscillation (cm)" empty={d.oscillationRows.length === 0}>
                <LineChart data={d.oscillationRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.oscillationRows, ['value'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" name="Vertical oscillation" stroke="var(--series-4)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Running power (W)" empty={d.powerRows.length === 0}>
                <LineChart data={d.powerRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.powerRows, ['value'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" name="Power" stroke="var(--series-3)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Fitness age (years, lower is better)" empty={d.profile.fitnessAge.length === 0}>
                <LineChart data={d.profile.fitnessAge.map(a => ({ date: shortDate(a.asOf), age: a.fitnessAge, achievable: a.achievableFitnessAge }))} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.profile.fitnessAge.map(a => ({ age: a.fitnessAge, achievable: a.achievableFitnessAge })), ['age', 'achievable'])} reversed />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="age" name="Fitness age" stroke="var(--chart-2)" strokeWidth={2} dot />
                  <Line type="monotone" dataKey="achievable" name="Achievable" stroke="var(--text-muted)" strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col xs={12}>
              <section className="surface">
                <h2>Heart rate zones{runningZones ? ` (${runningZones.sport.toLowerCase()})` : ''}</h2>
                {zones.length === 0 ? (
                  <p className="muted mb-0">No zones yet. They are fetched with the next sync.</p>
                ) : (
                  <div className="table-responsive">
                    <Table size="sm" className="trainer-table mb-0">
                      <thead>
                        <tr>
                          <th>Zone</th>
                          <th>Effort</th>
                          <th className="text-end">From (bpm)</th>
                          <th className="text-end">To (bpm)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zones.map(z => (
                          <tr key={z.zone}>
                            <td>Zone {z.zone}</td>
                            <td>{ZONE_NAMES[z.zone - 1]}</td>
                            <td className="text-end tabular">{z.from}</td>
                            <td className="text-end tabular">{z.to ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </section>
            </Col>
            <Col xs={12}>
              <DistanceVolumeChart />
            </Col>
            <Col md={6}>
              <ChartCard title="Resting heart rate (bpm)" empty={d.restingRows.length === 0}>
                <LineChart data={d.restingRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.restingRows, ['resting'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="resting" stroke="var(--chart-4)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="HRV (ms)" empty={d.hrvRows.length === 0}>
                <LineChart data={d.hrvRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(d.hrvRows, ['weekly', 'lastNight'])} />
                  <Tooltip />
                  <Line type="monotone" dataKey="weekly" name="7-day avg" stroke="var(--chart-3)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
            <Col md={6}>
              <ChartCard title="Body battery (high and low)" empty={d.batteryRows.length === 0}>
                <LineChart data={d.batteryRows} margin={MARGIN}>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="date" {...AXIS} minTickGap={16} />
                  <YAxis {...Y_AXIS} {...yAxisProps(undefined, [], { fixed: [0, 100] })} />
                  <Tooltip />
                  <Line type="monotone" dataKey="high" stroke="var(--chart-1)" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="low" stroke="var(--chart-2)" strokeWidth={2} dot={false} />
                </LineChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default FitnessPage;
